<template>
  <div>
    <div 
      class="pb-2 cursor-pointer layers-header"
    >
      <div
        class="d-flex align-items-center gap-2"
      >
        <p class="text-sm m-0">
          <b-icon 
            icon="layers" 
            class="mr-1 custom-green-icon"
          />
          <span class="font-semibold">Layers</span>
        </p>
        <span 
          v-if="selectionCount > 0"
          class="text-muted text-xs bg-light rounded p-1"
        >
          {{ selectionCount }} selected
        </span>
      </div>   
      <div 
        class="d-flex align-items-center"
      >
        <b-form-checkbox
          v-model="showDeletedLayers"
          switch
          class="mb-0"
        />
        <span class="text-xs text-muted mb-0">Deleted</span>
      </div>
    </div>
    <b-list-group>
      <draggable
        id="layers-list"
        :list="layersOrdered"
        group="layers"
        @end="dragEnd"
        @start="dragStart"
      >
        <b-list-group-item
          v-for="field in layersOrdered"
          :key="field.id"
          :data-v-key="field.id"
          button
          :active="isActive(field)"
          :variant="listItemVariant(field)"
          :class="[
            (!field.relatedCanvasObject && !showDeletedLayers) ? 'd-none' : 'd-flex','justify-content-between align-items-center px-2 py-1 primary-border-color',
            { 'font-weight-bold': !!field.relatedCanvasObject }
          ]"
          @click="(e) => setActiveObject(field.relatedCanvasObject, e)"
          @mouseenter="hoveredLayerId = field.id"
          @mouseleave="hoveredLayerId = null"
        >
          <div
            class="d-flex align-items-center layer-icons"
            style="min-width: 40px; gap: 8px"
          >
            <b-icon
              :icon="getIcon(field.relatedCanvasObject)"
              @click.stop="(e) => handleLayerVisibility(field.relatedCanvasObject, e)"
            />
            <span
              v-if="isTextLayer(field.relatedCanvasObject)"
              v-b-tooltip.hover="'Edit text'"
              class="text-layer-icon cursor-pointer d-flex align-items-center justify-content-center text-xs text-muted"
              @click.stop="editTextLayer(field.relatedCanvasObject)"
            >T</span>
            <img
              v-if="isSeeThrough(field.relatedCanvasObject) || hoveredLayerId === field.id || (isActive(field) && isSeeThrough(field.relatedCanvasObject))"
              v-b-tooltip.hover.viewport.noninteractive="isSeeThrough(field.relatedCanvasObject) ? 'Restore' : 'See-Through'"
              :src="isSeeThrough(field.relatedCanvasObject) ? '/images/see-through-active.svg' : '/images/see-through-hover.svg'"
              class="cursor-pointer"
              :class="{
                'see-through-selected': isActive(field),
              }"
              style="width: 16px; height: 16px;"
              @click.stop="toggleSeeThrough(field.relatedCanvasObject)"
            >
            <b-icon
              v-if="field.relatedCanvasObject?.isClippingMask"
              v-b-tooltip.hover.viewport.noninteractive="'Clipping mask'"
              icon="arrow90deg-down"
            />
          </div>

          <div class="flex-grow-1 text-center text-truncate px-2">
            <b-icon
              v-if="isOffScreen(field.relatedCanvasObject) && !isDuplicating"
              v-b-tooltip.hover.viewport.noninteractive="'This item is off-screen. Click to center it'"
              icon="bullseye"
              class="rounded-circle bg-primary mr-1"
              variant="light"
              @click="reposition(field.relatedCanvasObject)"
            />
            <b-tooltip
              v-if="!!field?.note"
              :target="`note-${field.id}`"
              triggers="hover"
            >
              {{ field.note }}
            </b-tooltip>
            <span
              :title="field.name"
              :disabled="isTooltipDisabled(field.name)"
              class="field-name"
            >
              {{ field.name }}
            </span>
            <b-icon
              v-if="!!field?.note"
              :id="`note-${field.id}`"
              icon="info-circle"
              class="ml-1"
            />
          </div>

          <div
            class="d-flex align-items-center justify-content-end"
            style="min-width: 24px"
          >
            <div
              v-if="isImageLayer(field) && !!field.relatedCanvasObject"
              v-b-tooltip.hover.viewport.noninteractive="'Replace image'"
              class="cursor-pointer"
              @click.stop="handleReplace(field)"
            >
              <i class="bi bi-repeat" />
            </div>
            <b-icon icon="grip-vertical" />
          </div>
        </b-list-group-item>
      </draggable>
    </b-list-group>

    <!-- Selection actions: split select and clear -->
    <div class="d-flex align-items-center justify-content-between mt-2 gap-2">
      <b-dropdown
        size="sm"
        variant="outline-primary"
        split
        text="Select product images"
        class="custom-split-dropdown flex-grow-1"
        @click="selectProductImages"
      >
        <b-dropdown-item @click="selectAllImages">
          Select all images
        </b-dropdown-item>
      </b-dropdown>

      <b-button
        :disabled="selectionCount === 0"
        size="sm"
        variant="outline-primary custom-secondary-button flex-grow-1"
        @click="deselectAllLayers"
      >
        Clear selection
      </b-button>
    </div>

    <div class="position-fixed apply-to-layer-section">
      <b-form-group class="d-flex mt-3">
        <div class="mr-2 custom-control custom-control-inline">
          Apply layer order to:
        </div>
        <SelectLayersTemplateDropdown
          ref="selectLayersDropdown"
          class="d-inline"
          :local-storage-key="'selected-templates-for-layers-order'"
          :clear-local-storage="true"
          @call-apply="applyOrder"
        />
      </b-form-group>
    </div>   
    <b-button-group 
      v-if="isMoreThanOneSelected"
      size="sm"
      class="w-100"
    >
      <b-button
        variant="outline-primary"
        size="sm"
        class="cursor-pointer mt-2 custom-secondary-button"
        @click.stop="handleMultipleLayersVisibility"
      >
        <b-icon
          :icon="getSelectionVisibilityIcon"
          class="mr-1"
        />
        Toggle selection visibility
      </b-button>
    </b-button-group>
    <b-button-group
      size="sm"
      class="d-flex flex-column w-100"
    >
      <b-button
        v-if="canBeDuplicated || isDuplicating"
        variant="outline-primary"
        size="sm"
        :disabled="isDuplicating"
        class="cursor-pointer custom-secondary-button text-xs mt-2"
        :class="{'opacity-50': isDuplicating }"
        @click.stop="handleDuplicateSelectedLayer"
      >
        <i
          v-if="!isDuplicating"
          class="bi bi-copy mr-1"
        />
        <b-spinner
          v-else
          small
          class="mr-1"
        />
        {{ isDuplicating ? 'Duplicating...' : 'Duplicate Layer' }}
      </b-button>

      <RestoreSeeThrough />

      <b-button
        v-if="canBeDeleted || isDeleting || deleteGroup"
        variant="outline-danger"
        size="sm"
        :disabled="isDeleting"
        class="cursor-pointer mt-2 custom-secondary-button text-xs"
        :class="{'opacity-50': isDeleting }"
        style="color: #dc3545;"
        @click.stop="handleDeleteLayer"
      >
        <b-spinner
          v-if="isDeleting"
          small
          class="mr-1"
        />
        <i class="bi bi-trash mr-1" />Delete {{ deleteGroup ? 'group' : 'layer' }}
      </b-button>
      <b-button
        v-if="canBeCopied || isCopying"
        variant="outline-primary"
        size="sm"
        :disabled="isCopying"
        class="cursor-pointer mt-2 custom-secondary-button text-xs font-semibold"
        :class="{'opacity-50': isCopying }"
        @click.stop="handleAddingToAllTemplates"
      >
        <i
          v-if="!isCopying"
          class="bi bi-file-earmark-plus mr-1"
        />
        <b-spinner
          v-else
          small
          class="mr-1"
        />
        {{ isCopying ? 'Adding...' : getCopyButtonText }}
      </b-button>
    </b-button-group>
  </div>
</template>

<script>
/* global previews */
import {mapGetters, mapActions} from 'vuex'
import draggable from 'vuedraggable'

import {editorControlEventBroker, EVENTS} from "@frontend/group/modules/editor-control-popup/event-broker";
import LayerDuplicationMixin from "@frontend/group/modules/layer-duplication/layerDuplicationMixin.js";
import LayerDeletionMixin from "@frontend/group/modules/layer-deletion/layerDeletionMixin";
import {
  ADDITIONAL_SAFE_ZONE_PREFIX_ELEMENT_ID,
  HORIZONTAL_GUIDE_LINE_PREFIX_ID, MASK_PREFIX_ID,
  SAFE_ZONE_PREFIX_ID,
  SMART_OBJECT,
  VERTICAL_GUIDE_LINE_PREFIX_ID
} from "@frontend/constants/templates-field-prefixes";
import {FIELD_TYPES} from "@frontend/constants/field-types";
import SelectLayersTemplateDropdown from "@frontend/components/modules/layouts-page/EditorControl/components/SelectLayersTemplateDropdown.vue";
import RestoreSeeThrough from "@frontend/components/modules/layouts-page/EditorControl/components/RestoreSeeThrough.vue";
import {ShowPageScopeCollector} from "@frontend/group/modules/scope-collectors/show-page-collector";
import {
  FIELD_TYPE_PRODUCT_IMAGE,
  FIELD_TYPE_BACKGROND_THEME_IMAGE,
  FIELD_TYPE_BACKGROND_THEME_IMAGE_MULTIPLE,
  FIELD_TYPE_BACKGROUND_IMAGE_UPLOAD,
  FIELD_TYPE_STATIC_IMAGE,
  FIELD_TYPE_UPLOAD_IMAGE,
  FIELD_TYPE_IMAGE_FROM_BACKGROUND
} from "@frontend/constants/type-fields-of-template";
import {sortSmartObjectMembersByTemplateOrder} from "@/common/preview/draw-smart-object";

const LAYER_VISIBILITY_ACTION = 'layerVisibility'

const BOTTOM_LIST_ITEMS = [SAFE_ZONE_PREFIX_ID, ADDITIONAL_SAFE_ZONE_PREFIX_ELEMENT_ID];

const getShowPageScope = () => ShowPageScopeCollector.get([
  'previews',
  'layoutChanges'
]);

export default {
  name: 'LayersModule',
  components: {
    draggable,
    SelectLayersTemplateDropdown,
    RestoreSeeThrough,
  },
  mixins: [LayerDuplicationMixin, LayerDeletionMixin],

  props: {
    isTabActive: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    layersOrdered: [],
    selectionCount: 0,
    draggedIndex: null,
    hiddenLayers: [],
    hiddenTopLayers: [],
    hiddenBottomLayers: [],
    selectedLayers: [],
    clickedLayerIndex: null,
    showDeletedLayers: true ,
    isDuplicating: false,
    activeObjects: [],
    isCopying: false,
    canvasSelectionTrigger: 0, // Reactive trigger for canvas selection changes
    hoveredLayerId: null,
    seeThroughRefreshKey: 0,
  }), 

  computed: {
    ...mapGetters('layout', [
      'layers',
      'target',
      'selectedInstance',
    ]),
    ...mapGetters('fileBrowserProductSelector', [
      'pickedItems',
      'selectedItems',
      'items'
    ]),
    getIcon() {
      return object => object?.visible ? 'eye' : 'eye-slash';
    },
    isSeeThrough() {
      this.seeThroughRefreshKey;
      return (object) => object?._isSeeThrough || false;
    },
    isMoreThanOneSelected() {
      return this.selectionCount > 1;
    },
    getSelectionVisibilityIcon() {
      const activeObjects = this.selectedInstance?.canvas?.getActiveObjects();
      if (!activeObjects.length) return 'eye-slash';

      const isCurrentlyVisible = activeObjects.every(obj => obj.visible);
      return isCurrentlyVisible ? 'eye-slash' : 'eye';
    },
    isImageLayer() {
      // Accept both field objects from the list and raw canvas objects
      // Prefer robust detection via fieldType, with element_id prefix fallback
      const IMAGE_FIELD_TYPES = [
        FIELD_TYPE_PRODUCT_IMAGE,
        FIELD_TYPE_BACKGROND_THEME_IMAGE,
        FIELD_TYPE_BACKGROND_THEME_IMAGE_MULTIPLE,
        FIELD_TYPE_BACKGROUND_IMAGE_UPLOAD,
        FIELD_TYPE_STATIC_IMAGE,
        FIELD_TYPE_UPLOAD_IMAGE,
        FIELD_TYPE_IMAGE_FROM_BACKGROUND,
      ];
      return (input) => {
        if (!input) return false;
        const obj = input?.relatedCanvasObject || input;
        const fieldType = obj?.fieldType || input?.fieldType || input?.field?.type;
        if (typeof fieldType === 'string' && IMAGE_FIELD_TYPES.includes(fieldType)) {
          return true;
        }
        const element_id = obj?.element_id || input?.element_id || obj?.id || input?.id;
        if (typeof element_id !== 'string') return false;
        // Fallback by element_id prefixes
        return (
          element_id.startsWith('additional') ||
          element_id.startsWith('background') ||
          element_id.startsWith('product_image') ||
          element_id.startsWith('bk_image') ||
          element_id.startsWith('add_bk_image')
        );
      }
    },

    canBeCopied() {
      this.canvasSelectionTrigger;
      
      // Check if we have active objects on canvas
      const activeObjects = this.selectedInstance?.canvas?.getActiveObjects();
      if (!activeObjects || activeObjects.length === 0) {
        return false;
      }

      // Check if there are other templates to copy to
      const previews = window.previews || [];
      const otherTemplates = previews.filter(preview => preview.id !== this.selectedInstance.id);
      if (otherTemplates.length === 0) {
        console.warn('canBeCopied: No other templates available');
        return false;
      }

      // Check if all selected objects have valid field types
      const invalidObjects = activeObjects.filter(obj => {
        // Skip objects that are part of a group or activeSelection
        if (obj.type === 'activeSelection' || (obj.group && obj.group.type === 'activeSelection')) return false;
        
        // Check if it's a smart object (mask)
        if (obj.id && obj.id.startsWith('mask_')) return true;
        
        // Check if object has fieldType - this is valid
        if (obj.fieldType) return false;
        
        // Check if it has fieldId and can be found in template - this is valid
        if (obj.fieldId) {
          const template = this.selectedInstance.template;
          if (template && template.fields) {
            const field = template.fields.find(field => field.id === obj.fieldId);
            if (field) return false; // Valid field found
          }
        }
        if (obj.selectable === false) return true;
        
        return false; // Assume valid if we can't determine otherwise
      });

      if (invalidObjects.length > 0) {
        console.warn('canBeCopied: Some objects have invalid field types:', invalidObjects);
        return false;
      }
      console.log('canBeCopied: All conditions met, button should show');
      return true;
    },
    
    getCopyButtonText() {
      const activeObjects = this.selectedInstance?.canvas?.getActiveObjects();
      const count = activeObjects ? activeObjects.length : 0;
      if (count === 0) return 'Add To All Templates';
      if (count === 1) return 'Add To All Templates';
      return `Add ${count} Layers To All Templates`;
    }
  },
  watch: {
    selectedInstance() {
      this.reorderLayers(this.selectedInstance.id);
      this.updateSelectionCount();
      
      // Re-setup canvas selection listener when selectedInstance changes
      this.$nextTick(() => {
        this.removeCanvasSelectionListener();
        this.setupCanvasSelectionListener();
      });
    },
    isTabActive() {
      this.rerenderList();
    },
    showDeletedLayers(val) {
      localStorage.setItem('showDeletedLayers', val);
    },
  },
  mounted() {
    const saved = localStorage.getItem('showDeletedLayers');
    if (saved !== null) {
      this.showDeletedLayers = saved === 'true';
    }
    
    // Add keyboard shortcuts for multi-selection
    this.addKeyboardShortcuts();
    
    // Set up canvas selection listener
    this.setupCanvasSelectionListener();

    this.setupSeeThroughListener();
  },
  
  beforeDestroy() {
    // Remove keyboard shortcuts
    this.removeKeyboardShortcuts();

    // Remove canvas selection listener
    this.removeCanvasSelectionListener();

    this.removeSeeThroughListener();
  },
  methods: {
    ...mapActions('fileBrowserProductSelector', [
      'setCurrentPickedItems',
      'pickItem'
    ]),
    isTextLayer(object) {
      if (!object) return false;
      const fieldType = object?.fieldType || object?.type;
      return fieldType === 'Multi Line Text';
    },
    editTextLayer(object) {
      if (!object) return;
      this.setActiveObject(object);
      editorControlEventBroker.fire(EVENTS.OPEN_TEXT_EDITOR, {
        instanceId: this.selectedInstance.id,
        objectId: object.id || object.element_id
      });
      const layoutSidebar = document.querySelector('layout-sidebar-ce').getVueInstance()
      layoutSidebar.tabIndex = 0;
    },
    isActive(element) {
      // If group or activeSelection is selected
      const activeObjects = this.selectedInstance.canvas.getActiveObjects();
      const elementObj = element.relatedCanvasObject;

      // 1. Object is directly selected
      if (activeObjects.includes(elementObj)) {
        return true;
      }

      // 2. Object is inside a selected group at any nesting level
      let parentGroup = elementObj?.group;
      while (parentGroup) {
        if (activeObjects.includes(parentGroup)) {
          return true;
        }

        parentGroup = parentGroup.group;
      }
      
      // 3. Check if object is part of an ActiveSelection
      const activeObject = this.selectedInstance.canvas.getActiveObject();
      if (activeObject && activeObject.type === 'activeSelection') {
        const selectionObjects = activeObject.getObjects();
        if (selectionObjects.includes(elementObj)) {
          return true;
        }
      }
      
      if (element.element_id.startsWith(SMART_OBJECT)) {
        return (this.target?.type === FIELD_TYPES.SMART_OBJECT && (this.target.smartObjectId == element.element_id || this.target.smartObjectId == element.id)) || this.target?.type === FIELD_TYPES.NEW_SMART_OBJECT;
      } else {
        const canvas = this.selectedInstance?.canvas;
        const activeObjects = canvas?.getActiveObjects() || [];
        this.selectionCount = activeObjects.length;
        const targetId = this.target?.element_id || this.target?.id;
        const elementId = element.relatedCanvasObject?.element_id || element.relatedCanvasObject?.id;
        if (!elementId || !targetId) {
          return false;
        }
        return activeObjects.includes(element.relatedCanvasObject) || elementId === targetId;
      }
    },
    setActiveObject(object, e) {
      if (!object) {
        console.log('setActiveObject: No object provided');
        return
      }
      
      // Check if Ctrl/Cmd key is pressed
      const isCtrlPressed = e && (e.ctrlKey || e.metaKey);
      const isShiftPressed = e && e.shiftKey;
      
      console.log('Key states:', { isCtrlPressed, isShiftPressed });
      
      if (isShiftPressed) {
        console.log('Range selection detected');
        const currentIndex = this.layersOrdered.findIndex(layer => layer.relatedCanvasObject === object);
        const activeObjects = this.selectedInstance.canvas.getActiveObjects();
        const anchorObject = activeObjects[activeObjects.length - 1] || object;

        const anchorIndex = this.layersOrdered.findIndex(layer => layer.relatedCanvasObject === anchorObject);
        if (currentIndex === -1 || anchorIndex === -1) return;

        const [start, end] = currentIndex < anchorIndex ? [currentIndex, anchorIndex] : [anchorIndex, currentIndex];
        const rangeObjects = this.layersOrdered
          .slice(start, end + 1)
          .map(layer => layer.relatedCanvasObject)
          .filter(obj => obj);

        const newActiveObjects = activeObjects
          .filter(obj => {
            const index = this.layersOrdered.findIndex(layer => layer.relatedCanvasObject === obj);
            return index < start || index > end;
          })
          .concat(rangeObjects);

        this.selectedInstance.canvas.discardActiveObject();
        newActiveObjects.forEach(obj => obj.setCoords());

        const selection = new fabric.ActiveSelection(
          [...new Set(newActiveObjects)],
          { canvas: this.selectedInstance.canvas }
        );

        selection.setCoords();
        this.selectedInstance.canvas.setActiveObject(selection);
        this.selectedInstance.canvas.requestRenderAll();
        this.selectionCount = selection.getObjects().length;
        
        // Update Vuex store target for multi-selection
        this.$store.commit('layout/SET_TARGET', selection);
        return;
      }

      if (isCtrlPressed) {
        console.log('Multi-selection detected');
        // getting active objects from canvas
        const activeObjects = this.selectedInstance.canvas.getActiveObjects();
        console.log('Current active objects:', activeObjects.length);
        
        // checking if object is already selected, if so, remove it from selection
        let newActiveObjects = [...activeObjects];
        
        // Use a more robust comparison method
        const isObjectSelected = activeObjects.some(obj => {
          // Compare by ID, element_id, or direct reference
          return (obj.id && obj.id === object.id) || 
                 (obj.element_id && obj.element_id === object.element_id) ||
                 obj === object;
        });
        
        if (isObjectSelected) {
          console.log('Object already selected, removing from selection');
          newActiveObjects = activeObjects.filter(obj => {
            // Use the same comparison logic for filtering
            return !((obj.id && obj.id === object.id) || 
                    (obj.element_id && obj.element_id === object.element_id) ||
                    obj === object);
          });
        } else {
          console.log('Object not selected, adding to selection');
          newActiveObjects = [...activeObjects, object];
        }
        
        console.log('New active objects count:', newActiveObjects.length);
        
        this.selectedInstance.canvas.discardActiveObject();
        
        if (newActiveObjects.length === 0) {
          // No objects selected, just clear the selection
          this.selectedInstance.canvas.requestRenderAll();
          this.selectionCount = 0;
          this.$store.commit('layout/SET_TARGET', null);
          return;
        }

        // Ensure all objects have setCoords called
        newActiveObjects.forEach(obj => {
          if (obj.setCoords) {
            obj.setCoords();
          }
        });

        try {
          const selection = new fabric.ActiveSelection(
            newActiveObjects,
            { canvas: this.selectedInstance.canvas }
          );

          selection.setCoords();
          this.selectedInstance.canvas.setActiveObject(selection);
          this.selectedInstance.canvas.requestRenderAll();
          this.selectionCount = newActiveObjects.length;
          
          // Update Vuex store target for multi-selection
          this.$store.commit('layout/SET_TARGET', selection);
          console.log('Multi-selection created successfully');
        } catch (error) {
          console.error('Error creating ActiveSelection:', error);
          // Fallback to single selection
          this.selectedInstance.canvas.setActiveObject(object);
          this.selectedInstance.canvas.requestRenderAll();
          this.selectionCount = 1;
          this.$store.commit('layout/SET_TARGET', object);
        }
        return;
      } 
      
      console.log('Single selection detected');
      if (object.element_id.startsWith(SMART_OBJECT)) {
        const preview = previews.find(preview => preview.id === this.selectedInstance.id)
        this.selectedInstance.canvas.fire('show:smart-object-editor', {instanceId: this.selectedInstance.id, preview: preview, smartObjectId: object.fieldId || object.element_id});
      } else {
        this.selectedInstance.canvas.setActiveObject(object)
        this.selectedInstance.canvas.renderAll() 
        
        // Update Vuex store target for single selection
        this.$store.commit('layout/SET_TARGET', object);
      }
      this.updateSelectionCount();
    },
    renderChanges () {
      const order = [...this.layersOrdered]
        .reverse()
        .filter(layer => layer.relatedCanvasObject);
      const layersIds = order.map(layer => layer.templateField?.id ?? layer.id);

      
      order.forEach(layer => {
        if (layer.relatedCanvasObject?.id.includes(MASK_PREFIX_ID)) {
          layer.relatedCanvasObject.bringToFront();
          sortSmartObjectMembersByTemplateOrder(layer.relatedCanvasObject, layersIds)
          return;
        } else if (layer.relatedCanvasObject?.groupName) {
          return;
        }
        
        (layer.relatedCanvasObject.group || layer.relatedCanvasObject).bringToFront();
      })
      
      const activeObject = this.selectedInstance.canvas.getActiveObject();

      if (activeObject && activeObject.id?.includes(MASK_PREFIX_ID)) {
        activeObject.bringToFront();
      }
      
      this.selectedInstance.canvas.renderAll()
      
      this.moveRulerGuideLinesToFront();
    },
    saveChanges () {
      const usersFieldsOrder = _.map(this.layersOrdered, 'id')
      const { persistHtmlTablesLayerOrderPreference } = ShowPageScopeCollector.get([
        'persistHtmlTablesLayerOrderPreference',
      ]);

      if (persistHtmlTablesLayerOrderPreference) {
        persistHtmlTablesLayerOrderPreference(this.selectedInstance.id, usersFieldsOrder);
      }

      this.selectedInstance.canvas.fire('layers:changed', {
        instance_id: this.selectedInstance.id,
        key: 'users_fields_order',
        value: usersFieldsOrder,
      })
    },
    saveChangesByTemplate(instanceId, usersFieldsOrder) {
      const { persistHtmlTablesLayerOrderPreference } = ShowPageScopeCollector.get([
        'persistHtmlTablesLayerOrderPreference',
      ]);

      if (persistHtmlTablesLayerOrderPreference) {
        persistHtmlTablesLayerOrderPreference(instanceId, usersFieldsOrder);
      }

      this.selectedInstance.canvas.fire('layers:changed', {
        instance_id: instanceId,
        key: 'users_fields_order',
        value: usersFieldsOrder,
      });
    },
    reorderLayers(instanceId) {
      const usersFieldsOrder = JSON.parse(this.selectedInstance.data.users_fields_order || null);

      this.selectedInstance.canvas.fire('layers:update', instanceId);

      if (usersFieldsOrder) {
        this.layersOrdered = _.sortBy(this.layers, layer => usersFieldsOrder.findIndex(id => id === layer.id));

        this.moveRulerGuideLinesToFront();

        return;
      }

      this.layersOrdered = this.layers;
      
      this.moveRulerGuideLinesToFront();
    },
    isTooltipDisabled(name) {
        return name?.length < 25;
    },
    rerenderList() {
      if (this.isTabActive) {
        this.reorderLayers(this.selectedInstance.id);
      }
    },
    listItemVariant(field) {
      const isBottomItem = BOTTOM_LIST_ITEMS.some(prefix => field.element_id.startsWith(prefix));

      if (isBottomItem) {
        return 'dark';
      }

      return field.relatedCanvasObject ? '' : 'light'
    },
    handleLayerVisibility(object, e) {
      const isCtrlPressed = e && (e.ctrlKey || e.metaKey);
      if (isCtrlPressed) {
        this.setActiveObject(object, e);
        return;
      }

      this.setActiveObject(object);
      
      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action: LAYER_VISIBILITY_ACTION,
        object
      });
    },
    isOffScreen (object) {
      if (object?.group?.type === 'activeSelection') {
        const selection = this.selectedInstance.canvas.getActiveObject();
        const rect = selection.getBoundingRect(true); 
        const objectRect = object.getBoundingRect(true);
        const objAbsCoords = {
          left: rect.left + rect.width/2 - objectRect.width/2 + object.left,
          top: rect.top + rect.height/2 - objectRect.height/2 + object.top,
          width: objectRect.width,
          height: objectRect.height
        }
        const canvas = this.selectedInstance.canvas;
        const canvasWidth = canvas.getWidth();
        const canvasHeight = canvas.getHeight();

        return objAbsCoords.left + objAbsCoords.width < 0 ||
          objAbsCoords.top + objAbsCoords.height < 0 ||
          objAbsCoords.left > canvasWidth ||
          objAbsCoords.top > canvasHeight;
      }
      return object?.selectable && !object.isOnScreen() && !isNaN(object.left + object.top)
    },
    reposition (object) {
      this.selectedInstance.canvas.centerObject(object)
      this.selectedInstance.canvas.fire('object:moving', { target: object })
      this.renderChanges()
    },
    moveRulerGuideLinesToFront() {
      this.selectedInstance.canvas
        .getObjects()
        .filter(
          obj => [HORIZONTAL_GUIDE_LINE_PREFIX_ID, VERTICAL_GUIDE_LINE_PREFIX_ID]
            .some(id => obj.id.startsWith(id))
        )
        .forEach(obj => obj.bringToFront());
    },
    updateSelectionCount() {
      this.selectionCount = this.selectedInstance.canvas.getActiveObjects().length;
    },
    handleMultipleLayersVisibility() {
      console.log('handleMultipleLayersVisibility');
      const activeObjects = this.selectedInstance.canvas.getActiveObjects();
      if (!activeObjects.length) return;

      const isCurrentlyVisible = activeObjects.every(obj => obj.visible);
      activeObjects.forEach(obj => {
        obj.visible = !isCurrentlyVisible;
        obj.setCoords();
      });

      this.selectedInstance.canvas.requestRenderAll();
      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action: LAYER_VISIBILITY_ACTION,
        object: activeObjects
      });
    },
    dragStart(e) {
      const listItems = [...document.querySelectorAll('#layers-list .list-group-item')];
      const selectedItems = listItems.filter(item => item.classList.contains('active'));
      const clickedItem = e.item;
      const clickedIndex = listItems.indexOf(clickedItem);
      // Store selectedLayers with original index, element, id, and layer
      this.selectedLayers = selectedItems.map(item => {
        const index = listItems.indexOf(item);
        let id = parseInt(item.getAttribute('data-v-key'));
        if (!id) {
          id = item.getAttribute('data-v-key');
        }
        const layer = this.layersOrdered.find(l => l.id === id);
        return {
          id,
          index,
          element: item,
          layer
        };
      });
      // Find which selected was clicked (by element)
      this.clickedLayerIndex = this.selectedLayers.findIndex(sel => sel.element === clickedItem);
      this.draggedIndex = clickedIndex;
      // Visually hide selected except clicked but only if we are dragging group of layers
      if (clickedItem.classList.contains('active')) {
        this.selectedLayers.forEach((sel, i) => {
          if (i === this.clickedLayerIndex) return;
          sel.element.style.transition = 'opacity 0.3s ease, height 0.3s ease, margin 0.3s ease, padding 0.3s ease';
          sel.element.style.opacity = '0';
          sel.element.style.height = '0';
          sel.element.style.margin = '0';
          sel.element.style.padding = '0';
          sel.element.classList.add('temporary-hidden');
        });
      }
    },
    dragEnd(e) {
      if (this.draggedIndex === null) return;
      // Use selectedLayers and clickedLayerIndex to separate top, clicked, bottom
      const selectedSorted = [...this.selectedLayers].sort((a, b) => a.index - b.index);
      const clicked = selectedSorted[this.clickedLayerIndex] || null;
      if (clicked === null || clicked === undefined) {
        this.renderChanges();
        this.saveChanges();
        return;
      }
      const top = selectedSorted.slice(0, this.clickedLayerIndex);
      const bottom = selectedSorted.slice(this.clickedLayerIndex + 1);
      const listItems = [...document.querySelectorAll('#layers-list .list-group-item')];

      const dropTarget = listItems.find(el =>
        el.classList.contains('active') && !el.classList.contains('temporary-hidden')
      );
      
      // filtered items
      const filteredItems = listItems.filter(el => !el.classList.contains('temporary-hidden'));

      const dropIndex = filteredItems.indexOf(dropTarget);
      // Remove all selected from layersOrdered
      const selectedIds = selectedSorted.map(s => s.id);
      this.layersOrdered = this.layersOrdered.filter(l => !selectedIds.includes(l.id));
      
     
      let insertIndex = dropIndex;
      insertIndex = Math.max(0, insertIndex);
      insertIndex = Math.min(this.layersOrdered.length, insertIndex);
      
      // Insert in preserved order
      this.layersOrdered.splice(
        insertIndex,
        0,
        ...top.map(x => x.layer),
        clicked.layer,
        ...bottom.map(x => x.layer)
      );
      // Restore visuals
      const hiddenItems = document.querySelectorAll('#layers-list .list-group-item.temporary-hidden');
      hiddenItems.forEach(item => {
        item.style.transition = '';
        item.style.opacity = '';
        item.style.height = '';
        item.style.margin = '';
        item.style.padding = '';
        item.classList.remove('temporary-hidden');
      });
      this.hiddenLayers = [];
      this.hiddenTopLayers = [];
      this.hiddenBottomLayers = [];
      this.draggedIndex = null;
      this.renderChanges();
      this.saveChanges();
    },
    handleReplace(field) {
      const object = field.relatedCanvasObject;
      if (!object) return;
      // If object is not selected, selecting it
      this.setActiveObject(object);
      // Fire event to replace image
      if (object.element_id.startsWith('product_image')) {
        // first wee need to open settings tab
        const layoutSidebar = document.querySelector('layout-sidebar-ce').getVueInstance()
        layoutSidebar.tabIndex = 0; // Open settings tab
        // and then fire event to replace image
        layoutSidebar.$nextTick(() => {
          // Wait for the tab to open
          editorControlEventBroker.fire(EVENTS.OPEN_REPLACE_PRODUCT_IMAGE);
        });
     
      } else {
        editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
          action: 'selectBackground'
        });
      }
     

      // Reset clickedLayerIndex to avoid issues with dragging after replacing
      this.clickedLayerIndex = null;
    },

    handleApplyOrderToSelectedTemplates() {
      const selectedTemplates = JSON.parse(localStorage.getItem('selected-templates-for-layers-order')) || [];
      if (!selectedTemplates.length) return;
      const currentInstance = this.selectedInstance;
      // getting current users_fields_order for current instance
      const currentUsersFieldsOrder = JSON.parse(currentInstance.data.users_fields_order || '[]');
      
      const templatesToUpdate = previews.filter(preview => {
        return selectedTemplates.includes(preview.id) && preview.id !== this.selectedInstance.id;
      });

      const {getUserLayersOrder} = ShowPageScopeCollector.get([
        'getUserLayersOrder',
      ]);

      if (!templatesToUpdate.length) return;
      templatesToUpdate.forEach(template => {
        const instance = this.$store.state.layout.currentPagePreviews.find(i => i.id === template.id);        
        if (instance) {
          let instanceUsersFieldsOrder;
          //when data undefined meant not yet load to preview, so get order by getUserLayersOrder
          if (instance.data.users_fields_order === undefined) {
            instanceUsersFieldsOrder = getUserLayersOrder(template.id).orders;
          } else {
            instanceUsersFieldsOrder = JSON.parse(instance.data.users_fields_order || '[]');
          }
          
          if (instanceUsersFieldsOrder.length === currentUsersFieldsOrder.length) {
            const sameFields = instanceUsersFieldsOrder.every(id => currentUsersFieldsOrder.includes(id));

            this.$store.commit('layout/SET_SELECTED_INSTANCE', instance)
            let reordered;
            if (sameFields) {
              // setting users_fields_order for instance
              reordered = this.layersOrdered.map(layer => layer.id);
            } else {
              // Handles the case where `ref` and `target` arrays have the same length,
              // but their field order differs (i.e., same number of elements, but mismatched positions).
              //
              // If the currently selected field does not exist in the target — it's skipped.
              //
              // Example 1:
              // Original template: ["text", "header", "arrow", "title"]
              // Target template:   ["arrow", "text", "header", "title"]
              // => "header" shifts from position 2 to 3
              //
              // Example 2:
              // Refs from checkbox: ["someNew1", "someNew2", "someNew3", "header"]
              // Target template:    ["someNew1", "someNew2", "header", "someNew3"]
              // => "header" is repositioned among new fields
              //
              // This function reorders `target` to match the order in `ref`, placing shared elements
              // according to `ref` and preserving the relative order of unmatched elements.
              function reorderMatchingInPlace(ref, target) {
                const sharedSet = new Set(ref);
                const shared = target.filter(el => sharedSet.has(el));
                const diff = target.filter(el => !sharedSet.has(el));

                const result = ref.map(el => shared.includes(el) ? el : null);
                return result.map(val => val !== null ? val : diff.shift());
              }

              reordered = reorderMatchingInPlace(currentUsersFieldsOrder, instanceUsersFieldsOrder);
            }
            this.saveChangesByTemplate(instance.id, reordered);
            // optionally apply the reordered order to the instance
            instance.data.users_fields_order = JSON.stringify(reordered);
            instance.canvas.fire('layers:update', instance.id);
          } else {
            //for now no good solution what to do if arrays have different length
          }
        } 
      });
      // setting current instance back
      this.$store.commit('layout/SET_SELECTED_INSTANCE', currentInstance);
    },
    applyOrder() {
      this.handleApplyOrderToSelectedTemplates();
      this.$refs.selectLayersDropdown.resetProperties();
    },
    
    addKeyboardShortcuts() {
      this.keyboardHandler = (e) => {
        // Ctrl/Cmd + A to select all layers
        if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
          e.preventDefault();
          this.selectAllLayers();
        }
        // Escape to deselect all
        // if (e.key === 'Escape') {
        //   e.preventDefault();
        //   this.deselectAllLayers();
        // }
      };
      
      document.addEventListener('keydown', this.keyboardHandler);
    },
    
    removeKeyboardShortcuts() {
      if (this.keyboardHandler) {
        document.removeEventListener('keydown', this.keyboardHandler);
      }
    },
    
    selectAllLayers() {
      const canvas = this.selectedInstance?.canvas;
      if (!canvas) return;
      
      const objects = canvas.getObjects().filter(obj => obj.selectable !== false);
      if (objects.length === 0) return;
      
      canvas.discardActiveObject();
      const selection = new fabric.ActiveSelection(objects, { canvas });
      selection.setCoords();
      canvas.setActiveObject(selection);
      canvas.requestRenderAll();
      this.selectionCount = objects.length;
      
      // Update Vuex store
      this.$store.commit('layout/SET_TARGET', selection);
    },
    
    deselectAllLayers() {
      const canvas = this.selectedInstance?.canvas;
      if (!canvas) return;
      
      canvas.discardActiveObject();
      canvas.requestRenderAll();
      this.selectionCount = 0;
      
      // Update Vuex store
      this.$store.commit('layout/SET_TARGET', null);
    },

    selectAllImages() {
      const canvas = this.selectedInstance?.canvas;
      if (!canvas) return;
      
      const objects = canvas
        .getObjects()
        .filter(obj => obj.selectable !== false && this.isImageLayer(obj));
      
      if (objects.length === 0) {
        // Nothing to select
        return;
      }
      
      canvas.discardActiveObject();
      try {
        const selection = new fabric.ActiveSelection(objects, { canvas });
        selection.setCoords();
        canvas.setActiveObject(selection);
        canvas.requestRenderAll();
        this.selectionCount = objects.length;
        this.$store.commit('layout/SET_TARGET', selection);
      } catch (e) {
        // Fallback to single selection if ActiveSelection fails
        canvas.setActiveObject(objects[0]);
        canvas.requestRenderAll();
        this.selectionCount = 1;
        this.$store.commit('layout/SET_TARGET', objects[0]);
      }
    },

    selectProductImages() {
      const canvas = this.selectedInstance?.canvas;
      if (!canvas) return;

      const objects = canvas
        .getObjects()
        .filter(obj => obj.selectable !== false && obj.fieldType === FIELD_TYPE_PRODUCT_IMAGE);

      if (objects.length === 0) {
        // Nothing to select
        return;
      }

      canvas.discardActiveObject();
      try {
        const selection = new fabric.ActiveSelection(objects, { canvas });
        selection.setCoords();
        canvas.setActiveObject(selection);
        canvas.requestRenderAll();
        this.selectionCount = objects.length;
        this.$store.commit('layout/SET_TARGET', selection);
      } catch (e) {
        canvas.setActiveObject(objects[0]);
        canvas.requestRenderAll();
        this.selectionCount = 1;
        this.$store.commit('layout/SET_TARGET', objects[0]);
      }
    },

    setupCanvasSelectionListener() {
      // Listen to all canvas instances for selection changes
      const previews = window.previews || [];
      
      this.canvasSelectionHandler = () => {
        // Trigger reactive update when canvas selection changes
        this.canvasSelectionTrigger++;
      };
      
      // Set up listeners for all canvas instances
      previews.forEach(preview => {
        if (preview.canvas) {
          preview.canvas.on('selection:created', this.canvasSelectionHandler);
          preview.canvas.on('selection:updated', this.canvasSelectionHandler);
          preview.canvas.on('selection:cleared', this.canvasSelectionHandler);
        }
      });
    },
    
    removeCanvasSelectionListener() {
      if (!this.canvasSelectionHandler) return;
      
      // Remove listeners from all canvas instances
      const previews = window.previews || [];
      previews.forEach(preview => {
        if (preview.canvas) {
          preview.canvas.off('selection:created', this.canvasSelectionHandler);
          preview.canvas.off('selection:updated', this.canvasSelectionHandler);
          preview.canvas.off('selection:cleared', this.canvasSelectionHandler);
        }
      });
    },

    toggleSeeThrough(object) {
      if (!object) {
        console.warn('No object provided for see-through toggle');
        return;
      }

      const isSeeThrough = object._isSeeThrough;

      if (!isSeeThrough) {
        object._originalOpacity = object.opacity !== undefined ? object.opacity : 1;
        object._isSeeThrough = true;

        object.set({
          opacity: 0.4,
          evented: false,
          selectable: false,
        });
      } else {
        const restoredOpacity = object._originalOpacity !== undefined ? object._originalOpacity : 1;

        object.set({
          opacity: restoredOpacity,
          evented: true,
          selectable: true,
        });

        object._isSeeThrough = false;
        object._originalOpacity = undefined;
      }

      if (object.canvas) {
        object.canvas.fire('see-through:changed', object);
        object.canvas.requestRenderAll();
      }

      this.seeThroughRefreshKey++;
    },

    setupSeeThroughListener() {
      const previews = window.previews || [];

      this.seeThroughHandler = () => {
        this.seeThroughRefreshKey++;
      };

      previews.forEach(preview => {
        if (preview.canvas) {
          preview.canvas.on('see-through:changed', this.seeThroughHandler);
        }
      });
    },

    removeSeeThroughListener() {
      if (!this.seeThroughHandler) return;

      const previews = window.previews || [];
      previews.forEach(preview => {
        if (preview.canvas) {
          preview.canvas.off('see-through:changed', this.seeThroughHandler);
        }
      });
    },
  }
}
</script>

<style scoped lang="scss">
.temporary-hidden {
  display: none !important;
  visibility: hidden;
}

.cursor-pointer {
  cursor: pointer;
}

.layer-icons {
  i.bi-transparency {
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.7;
    }
  }
}

.see-through-selected {
  filter: brightness(0) invert(1);
}

.text-layer-icon {
  width: 16px;
  height: 16px;
  font-weight: bold;
  font-size: 14px;
  color: black;
  border-radius: 2px;
  text-align: center;
  line-height: 14px;
  display: inline-block;
  margin-right: 4px;
}

.apply-to-layer-section{
  bottom: 44px;
  left: 0px;
  z-index: 999;
}
.custom-green-icon {
  color: #209c86;
  font-size: 1rem !important;
}
.layers-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.field-name {
  font-size: 0.8rem !important;
}
.primary-border-color {
  border-color: #e2e8f0 !important;
}
::v-deep .custom-secondary-button {
  border-color: #e2e8f0 !important;
  box-shadow: none !important;
  color: #020817;
  border-radius: 6px !important;
  font-size: 12px !important;
  font-weight: 600; 
}
::v-deep .custom-secondary-button:hover {
  background-color: #F7F9FB !important;
}
.custom-border-top {
  border-top: 1px solid #e2e8f0;
}
::v-deep .see-through-container button {
  border-color: #e2e8f0 !important;
  box-shadow: none !important;
  color: #020817;
  border-radius: 6px !important;
  font-weight: 600;
  font-size: 0.75rem !important;
  margin-top: 0 !important;
}
::v-deep .see-through-container button:hover {
  background-color: #F7F9FB !important;
}
::v-deep .see-through-container span {
  border-color: #e2e8f0 !important;
  color: #020817;
  font-weight: 600;
  font-size: 0.75rem !important;
  line-height: 0.7rem !important;
}
::v-deep .custom-split-dropdown.btn-group > .btn {
  font-size: 12px;
  font-weight: 600;
  border-color: #e2e8f0 !important;
  box-shadow: none !important;
  color: #020817;
}

::v-deep .custom-split-dropdown.btn-group > .btn:hover,
::v-deep .custom-split-dropdown.btn-group > .btn:focus,
::v-deep .custom-split-dropdown.btn-group > .btn:active {
  background-color: #F7F9FB !important;
  box-shadow: none !important;
}

::v-deep .custom-split-dropdown.btn-group > .btn:first-child {
  border-right: 0 !important;
  border-top-left-radius: 6px !important;
  border-bottom-left-radius: 6px !important;
}

::v-deep .custom-split-dropdown.btn-group > .dropdown-toggle-split {
  border-left: 0 !important;
  border-top-right-radius: 6px !important;
  border-bottom-right-radius: 6px !important;
}

::v-deep .custom-split-dropdown .dropdown-toggle-split::before {
  display: none;
}
</style>
